/**
 * Every rules version (newest first) for the 版本 page, following nextToken to the end — there are
 * only a handful — and telling a denial apart from other failures.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  getCpaClient,
  ListRecommendationRuleVersionsCommand,
  type RuleVersionSummary,
} from '../client/CpaClient';
import { isAccessDenied } from '../utils/programs';

export type VersionsState =
  | { kind: 'loading' }
  | { kind: 'ready'; versions: RuleVersionSummary[] }
  | { kind: 'denied' }
  | { kind: 'error'; message: string };

export async function listAllVersions(maxPages = 20): Promise<RuleVersionSummary[]> {
  const versions: RuleVersionSummary[] = [];
  let nextToken: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const out = await getCpaClient().send(new ListRecommendationRuleVersionsCommand({ nextToken }));
    versions.push(...(out.versions ?? []));
    nextToken = out.nextToken;
    if (!nextToken) return versions;
  }
  throw new Error(`ListRecommendationRuleVersions did not finish within ${maxPages} pages`);
}

export function useRuleVersions(): { state: VersionsState; reload: () => void } {
  const [state, setState] = useState<VersionsState>({ kind: 'loading' });

  const reload = useCallback(() => {
    setState({ kind: 'loading' });
    listAllVersions()
      .then((versions) => setState({ kind: 'ready', versions }))
      .catch((e: unknown) =>
        setState(
          isAccessDenied(e)
            ? { kind: 'denied' }
            : { kind: 'error', message: e instanceof Error ? e.message : String(e) },
        ),
      );
  }, []);

  useEffect(reload, [reload]);

  return { state, reload };
}
